import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { OnboardingPaso1Schema, type OnboardingPaso1DTO } from '@shared/validators';
import { useState } from 'react';
import { ArrowRight } from 'lucide-react';
import { traducirErrorBackend } from '@renderer/lib/mensajesError';

interface Props {
  onSiguiente: () => void;
}

export function Step1Negocio({ onSiguiente }: Props) {
  const [enviando, setEnviando] = useState(false);
  const [errorBackend, setErrorBackend] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<OnboardingPaso1DTO>({
    resolver: zodResolver(OnboardingPaso1Schema),
    mode: 'onChange',
    defaultValues: { nombre: '', rfc: '', direccion: '', telefono: '', email: '' },
  });

  const onSubmit = async (data: OnboardingPaso1DTO) => {
    setEnviando(true);
    setErrorBackend(null);
    const res = await window.api.onboarding.guardarPaso(1, data);
    setEnviando(false);
    if (res.ok) {
      onSiguiente();
    } else {
      setErrorBackend(traducirErrorBackend(res.error));
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="mx-auto max-w-xl space-y-6">
      <div className="text-center">
        <h1 className="text-2xl font-bold">Cuéntanos sobre tu negocio</h1>
        <p className="mt-2 text-slate-500">
          Solo el nombre es obligatorio. Lo demás puedes agregarlo después.
        </p>
      </div>

      <div className="card space-y-4">
        <div>
          <label className="label" htmlFor="nombre">Nombre del negocio *</label>
          <input
            id="nombre"
            className={`input ${errors.nombre ? 'input-error' : ''}`}
            placeholder="Mi Restaurante"
            autoFocus
            {...register('nombre')}
          />
          {errors.nombre && <div className="error-text">{errors.nombre.message}</div>}
        </div>

        <div>
          <label className="label" htmlFor="rfc">RFC (opcional)</label>
          <input
            id="rfc"
            className={`input uppercase ${errors.rfc ? 'input-error' : ''}`}
            placeholder="XAXX010101000"
            {...register('rfc')}
          />
          {errors.rfc && <div className="error-text">{errors.rfc.message}</div>}
        </div>

        <div>
          <label className="label" htmlFor="direccion">Dirección (opcional)</label>
          <input id="direccion" className="input" {...register('direccion')} />
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <label className="label" htmlFor="telefono">Teléfono (opcional)</label>
            <input id="telefono" className="input" {...register('telefono')} />
          </div>
          <div>
            <label className="label" htmlFor="email">Email (opcional)</label>
            <input id="email" type="email" className={`input ${errors.email ? 'input-error' : ''}`} {...register('email')} />
            {errors.email && <div className="error-text">{errors.email.message}</div>}
          </div>
        </div>
      </div>

      {errorBackend && (
        <div className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700" role="alert">
          {errorBackend}
        </div>
      )}

      <div className="flex justify-end">
        <button
          type="submit"
          className="btn-primary btn-lg"
          disabled={!isValid || enviando}
        >
          Siguiente
          <ArrowRight className="h-5 w-5" />
        </button>
      </div>
    </form>
  );
}
